import { Lock, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';

interface CILockedBannerProps {
  lockedBy?: {
    userId: string;
    userName: string;
    userEmail: string;
  };
  lockedAt?: string;
  lockedReason?: string;
  userRole?: 'user' | 'companyAdmin' | 'consultant';
  onUnlockRequest?: () => void; // For clients to request unlock
  onUnlock?: () => void; // For consultants to unlock directly
}

export function CILockedBanner({
  lockedBy,
  lockedAt,
  lockedReason,
  userRole,
  onUnlockRequest,
  onUnlock,
}: CILockedBannerProps) {
  // Format the locked timestamp
  const formatDate = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return isoString;
    }
  };

  const isConsultant = userRole === 'consultant';

  return (
    <Alert variant="default" className="border-orange-200 bg-orange-50 mb-6">
      <Lock className="size-5 text-orange-600" />
      <AlertTitle className="text-orange-900 flex items-center justify-between">
        <span>Requirements Locked</span>
        {isConsultant && onUnlock && (
          <Button
            variant="outline"
            size="sm"
            onClick={onUnlock}
            className="text-xs h-7 ml-auto"
          >
            <Lock className="size-3 mr-1" />
            Unlock CI
          </Button>
        )}
        {!isConsultant && onUnlockRequest && (
          <Button
            variant="outline"
            size="sm"
            onClick={onUnlockRequest}
            className="text-xs h-7 ml-auto"
          >
            <AlertCircle className="size-3 mr-1" />
            Request Changes
          </Button>
        )}
      </AlertTitle>
      <AlertDescription className="text-orange-800 text-sm mt-2 space-y-2">
        <p>
          {isConsultant 
            ? 'This configuration item is locked for implementation. Discovery answers cannot be edited by the client unless you unlock it.'
            : 'This configuration item is locked for implementation. Discovery answers cannot be edited. Contact your consultant if changes are needed.'
          }
        </p>
        {lockedBy && (
          <div className="text-xs mt-2 pt-2 border-t border-orange-200">
            <p>
              <strong>Locked by:</strong> {lockedBy.userName}
            </p>
            {lockedAt && (
              <p>
                <strong>Locked on:</strong> {formatDate(lockedAt)}
              </p>
            )}
            {lockedReason && (
              <p>
                <strong>Reason:</strong> {lockedReason}
              </p>
            )}
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
